import { useRef } from "react";
import dynamic from "next/dynamic";
import CityHeroSection from "@/components/city/sections/CityHeroSection";
import FactsSection from "@/components/tour/sections/variantB/FactsSection";

// Lazy load below-the-fold components
const CityTextImageSection = dynamic(() => import("@/components/city/sections/CityTextImageSection"));
const CityToursSection = dynamic(() => import("@/components/city/sections/CityToursSection"));
const CityPhotosSection = dynamic(() => import("@/components/city/sections/CityPhotosSection"));
const CityReviewsSection = dynamic(() => import("@/components/city/sections/CityReviewsSecition"));
const CityBlogSection = dynamic(() => import("@/components/city/sections/CityBlogSection"));

export default function CityPage({ data }) {
    const scrollToDivRef = useRef(null);

    const { reviewOverlay, page, socialLinks, cityDefaults } = data?.data || {};
    const {
        heroImage,
        textAndImageImage,
        tours,
        images,
        reviews,
        reviewsLogos,
        data: pageData,
        name,
        historicalTourCardImage,
    } = page || {};
    const {
        hero,
        textAndImage,
        tours: toursData,
        images: imagesData,
        reviews: reviewsData,
        historicalCard,
        facts,
        blog
    } = pageData || {};

    return (
        <div>
            {/* Hero Section (Above-the-Fold) */}
            <CityHeroSection
                reviewOverlayData={reviewOverlay}
                scrollToDivRef={scrollToDivRef}
                heroMedia={heroImage}
                backgroundImageUrl={heroImage?.url}
                socialLinks={socialLinks}
                subtitle={hero?.subtitle}
                heroTitle={hero?.title}
                breadcrumbds={[
                    { title: "Home", link: "/" },
                    { title: name },
                ]}
                dropdownData={pageData?.showDropdown ? pageData?.dropdowns : []}
            />

            {/* Text and Image Section */}
                <div ref={scrollToDivRef}>
                    {(textAndImage?.title || textAndImage?.description) && textAndImageImage && (
                        <CityTextImageSection
                            reviewOverlayData={reviewOverlay}
                            title={textAndImage?.title}
                            image={textAndImageImage}
                            description={textAndImage?.description}
                            buttons={textAndImage?.buttons}
                        />
                    )}
                </div>

            {/* Tours Section */}
                <div id={'all-tours'}>
                    <CityToursSection
                        title={toursData?.title}
                        subtitle={toursData?.subtitle}
                        tours={tours}
                        cta={{
                            primaryText: toursData?.ctaPrimaryText,
                            secondaryText: toursData?.ctaSecondaryText,
                            defaultPrimaryText: cityDefaults?.data?.tours?.ctaPrimaryText,
                            defaultSecondaryText: cityDefaults?.data?.tours?.ctaSecondaryText,
                            hideSecondary: !(toursData?.showSecondaryCta ?? cityDefaults?.data?.tours?.showSecondaryCta),
                            disablePopup: toursData?.disableBookingPopup ?? cityDefaults?.data?.tours?.disableBookingPopup,
                        }}
                        historicalTour={{
                            rating: historicalCard?.rating,
                            title: historicalCard?.title,
                            description: historicalCard?.description,
                            buttonText: historicalCard?.buttonText,
                            buttonLink: historicalCard?.buttonLink,
                            image: historicalTourCardImage,
                        }}
                    />
                </div>

            <div className="container_small_slider" style={{marginTop:'50px'}}>
                <FactsSection
                    facts={facts}
                    cityName={name}
                    factFirstIcon={page?.factFirstIcon}
                    factSecondIcon={page?.factSecondIcon}
                    factThirdIcon={page?.factThirdIcon}
                />
            </div>

            {/* Photos Section */}
                {images?.length > 0 && (
                    <CityPhotosSection
                        title={imagesData?.title}
                        subtitle={imagesData?.subtitle}
                        photos={images}
                        cityName={name}
                        defaultTitle={cityDefaults?.data?.images?.title}
                        defaultSubtitle={cityDefaults?.data?.images?.subtitle}
                    />
                )}

            {/* Reviews Section */}
                {reviews?.length > 0 && (
                    <CityReviewsSection
                        logos={reviewsLogos}
                        rating={reviewsData?.averageRating}
                        ratingSubtitle={reviewsData?.subtitle}
                        reviews={reviews}
                    />
                )}

            {/* Blog Section — blog.enabled + blog.posts come from CMS/backend */}
                {blog?.enabled && blog?.posts?.length > 0 && (
                    <CityBlogSection
                        cityName={name}
                        posts={blog.posts}
                        readAllLink={blog.readAllLink}
                        readAllText={blog.readAllText}
                    />
                )}
        </div>
    );
}
